import React, { useMemo, useState } from 'react';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import type { AIModelCatalog as AIModelCatalogData, AIModelKind } from 'services/api/aiSettingsClient';

const KIND_LABELS: Record<AIModelKind, string> = {
  text: 'Text',
  realtime: 'Realtime',
  transcription: 'Transcription',
  image: 'Image',
  tts: 'Speech',
  embedding: 'Embedding',
  moderation: 'Moderation',
  other: 'Other',
};

type Props = {
  catalog: AIModelCatalogData;
  isRefreshing: boolean;
  onRefresh: () => void;
};

const AIModelCatalog: React.FC<Props> = ({ catalog, isRefreshing, onRefresh }) => {
  const [kind, setKind] = useState<AIModelKind | 'all'>('all');
  const [query, setQuery] = useState('');
  const counts = useMemo(() => {
    const result: Partial<Record<AIModelKind, number>> = {};
    catalog.models.forEach((model) => { result[model.kind] = (result[model.kind] ?? 0) + 1; });
    return result;
  }, [catalog.models]);
  const visible = catalog.models.filter((model) => (
    (kind === 'all' || model.kind === kind) && model.id.toLowerCase().includes(query.trim().toLowerCase())
  ));

  return (
    <Card className="w-full max-w-none" maxWidth="none" disableAnimation>
      <CardContent className="items-stretch space-y-4 py-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground" data-testid="ai-model-catalog-status">
            {catalog.fetched_at
              ? `${catalog.models.length} models available to the configured OpenAI key · checked ${new Date(catalog.fetched_at).toLocaleString()}`
              : 'The model list hasn\'t been loaded yet.'}
          </p>
          <Button type="button" size="auto" variant="secondary" onClick={onRefresh} disabled={isRefreshing}>
            {isRefreshing ? 'Refreshing...' : 'Refresh from OpenAI'}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          OpenAI's model list reports account access, not supported processing tiers or parameter combinations.
          Model types below are inferred from their names. Changing a text feature's model or processing settings
          runs a small compatibility check when saved; refreshing this list does not run model tests.
        </p>
        {catalog.last_error ? (
          <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
            {`Last refresh failed: ${catalog.last_error}`}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            aria-label="Search models"
            placeholder="Search models"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="max-w-xs"
          />
          {(['all', ...Object.keys(KIND_LABELS)] as Array<AIModelKind | 'all'>)
            .filter((option) => option === 'all' || counts[option])
            .map((option) => (
              <Button
                key={option}
                type="button"
                size="auto"
                variant={kind === option ? 'default' : 'secondary'}
                className="min-h-0 px-3 py-1.5 text-xs"
                aria-pressed={kind === option}
                onClick={() => setKind(option)}
              >
                {option === 'all' ? `All ${catalog.models.length}` : `${KIND_LABELS[option]} ${counts[option]}`}
              </Button>
            ))}
        </div>
        {visible.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="ai-model-catalog">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="py-2 pr-4 font-semibold">Model</th>
                  <th className="py-2 pr-4 font-semibold">Type</th>
                  <th className="py-2 pr-4 font-semibold">Owner</th>
                  <th className="py-2 font-semibold">Released</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((model) => (
                  <tr key={model.id} className="border-t border-border">
                    <td className="py-2 pr-4 font-mono text-xs text-foreground">{model.id}</td>
                    <td className="py-2 pr-4">{KIND_LABELS[model.kind]}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{model.owned_by}</td>
                    <td className="py-2 text-muted-foreground">
                      {model.created ? new Date(model.created * 1000).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No models match.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default AIModelCatalog;
